import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { Info, WarningCircle } from '@phosphor-icons/react';

type StatusNoticeProps = {
  message: string;
  severity?: 'error' | 'warning' | 'info';
  className?: string;
  action?: React.ReactNode;
};

export const StatusNotice: React.FC<StatusNoticeProps> = ({
  message,
  severity = 'error',
  className = '',
  action,
}) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const [isTruncated, setIsTruncated] = useState(false);
  const messageRef = useRef<HTMLSpanElement>(null);

  useEffect(() => {
    setIsExpanded(false);
  }, [message]);

  useLayoutEffect(() => {
    if (isExpanded) {
      return;
    }

    const element = messageRef.current;
    if (!element) {
      return;
    }

    const checkTruncation = () => {
      setIsTruncated(element.scrollHeight > element.clientHeight + 1);
    };

    checkTruncation();

    const observer = new ResizeObserver(checkTruncation);
    observer.observe(element);

    return () => {
      observer.disconnect();
    };
  }, [message, isExpanded]);

  const isWarning = severity === 'warning';
  const isInfo = severity === 'info';
  const containerStyles = isInfo
    ? 'border-notice-info-border bg-notice-info text-notice-info-color'
    : isWarning
      ? 'border-notice-warning-border bg-notice-warning text-notice-warning-color'
      : 'border-glyph-critical/20 bg-glyph-critical/10 text-glyph-critical';
  const Icon = isInfo ? Info : WarningCircle;

  return (
    <div
      className={`${containerStyles} flex items-start gap-2 rounded-lg border px-3 py-2 text-sm ${className}`}
      role="alert"
    >
      <Icon size={16} weight="fill" className="mt-0.5 shrink-0" />
      <div className="min-w-0 flex-1">
        <span ref={messageRef} className={isExpanded ? undefined : 'line-clamp-2'}>
          {message}
        </span>
        {isTruncated && !isExpanded ? (
          <button
            type="button"
            onClick={() => setIsExpanded(true)}
            className="mt-0.5 inline-block text-sm font-medium text-glyph-secondary underline hover:text-glyph-primary"
          >
            View More
          </button>
        ) : null}
      </div>
      {action}
    </div>
  );
};
